import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { exportStaffingPlan, getStaffingPlan, listScenarios, setStaffingCell } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { useCostLock } from './costLock'
import VisibilityMenu from './VisibilityMenu'
import { byExplicitOrder, num, projectShortName, projectTitle, reordered } from './sheet'

/**
 * "Disponibilidad -- por semana": the staffing plan week by week, and the one
 * place it can be rebalanced.
 *
 * A person's cell is the sum of every project that wants them that week. Each
 * project alone is capped at what their week holds, so the only way that sum
 * goes over is by being wanted in two places at once -- which is precisely the
 * problem this grid exists to surface, and why an over-committed cell is
 * painted rather than prevented.
 *
 * Expanding a person opens a row per project, and those cells write straight
 * back to that project's dotacion. That is the whole point: a PMO rebalancing
 * somebody's week should not have to open four projects in four tabs and hope
 * they still add up. Typing over the ceiling trims to it and shows the trimmed
 * figure, rather than refusing a number somebody already typed.
 *
 * Adding somebody to a project for the first time deliberately stays on the
 * project's own tab, where their role and dedication get set along with it.
 */
const WINDOWS = [5, 15, 28, 52]

const mondayOf = (d) => {
  const out = new Date(d)
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7))   // Sunday counts as 7
  out.setHours(12)                    // midday, so a DST shift cannot slip a day
  return out
}
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  + `-${String(d.getDate()).padStart(2, '0')}`
const weekLabel = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`

/** The Monday-aligned start and week count that cover exactly one calendar
 *  month -- a short month can fit in 4 weeks, a long one spanning two
 *  Mondays' worth of slack needs 6, so this is computed, never a constant. */
function monthWindow(year, month) {
  const first = mondayOf(new Date(year, month - 1, 1))
  const lastDay = new Date(year, month, 0)
  const lastMonday = mondayOf(lastDay)
  const count = Math.round((lastMonday - first) / (7 * 24 * 3600 * 1000)) + 1
  return { start: isoDay(first), weeks: count }
}

export default function StaffingWeeks() {
  const { t } = useTranslation(['clevel', 'common'])
  const { password } = useCostLock()
  const { user } = useAuth()
  const [weeks, setWeeks] = useState(WINDOWS[0])
  const [start, setStart] = useState(() => isoDay(mondayOf(new Date())))
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [fullscreen, setFullscreen] = useState(false)
  const [expanded, setExpanded] = useState(() => new Set())
  const [hiddenMembers, setHiddenMembers] = useState(() => new Set())
  const [memberOrder, setMemberOrder] = useState([])
  const [dragging, setDragging] = useState(null)
  const [saving, setSaving] = useState(null)      // "projectId:memberId:monday"
  const [scenarioId, setScenarioId] = useState('')
  const [scenarios, setScenarios] = useState([])

  const k = useCallback((suffix, opts) => t(`capacity.weeksView.${suffix}`, opts), [t])

  const roles = user?.roles || (user?.role ? [user.role] : [])
  // c_level EXACTLY -- mirrors require_exactly("c_level") on scenario_id
  // (routers/capacity.py). While a scenario is selected, cells edit THAT
  // scenario's own phantom project instead of the real one (same
  // setStaffingCell call, same project_id already in the payload from the
  // scenario-scoped `data` this component loaded) -- gated to c_level on the
  // backend too (auth_utils.require_staffing_edit_access), never admin/pm_po.
  const canScenarios = roles.includes('c_level')
  const canEdit = scenarioId ? canScenarios : ['admin', 'pm_po'].some(r => roles.includes(r))

  useEffect(() => {
    if (!canScenarios) return
    listScenarios().then(setScenarios).catch(() => {})
  }, [canScenarios])

  // {team_member_id: fire_monday} for the scenario currently selected, so a
  // fired person's own future cells can be locked -- editing them back would
  // silently un-fire them outside the fire-action mechanism (and its
  // moved_hours bookkeeping, see scenario_staffing.py) entirely. Rounded down
  // to that week's own Monday, same as the backend's own
  // gantt_calendar.monday_of(action.fire_date) -- a departure mid-week still
  // blocks the WHOLE week it falls in, never half of it.
  const fireDates = scenarioId
    ? Object.fromEntries((scenarios.find(s => String(s.id) === String(scenarioId))?.fire_actions || [])
        .map(a => [a.team_member_id, isoDay(mondayOf(new Date(`${a.fire_date}T12:00:00`)))]))
    : {}
  const isFired = (teamMemberId, monday) => {
    const fireMonday = fireDates[teamMemberId]
    return !!fireMonday && monday >= fireMonday
  }

  const load = useCallback(() => {
    getStaffingPlan({ weeks, start, costBasis: 'company', scenarioId: canScenarios ? scenarioId : '' }, password)
      .then(d => { setData(d); setError('') })
      .catch(err => setError(err.response?.data?.detail || k('loadFailed')))
  }, [weeks, start, scenarioId, canScenarios, password, k])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!fullscreen) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [fullscreen])

  const clearOrder = () => setMemberOrder([])
  const goWindow = (size) => { setWeeks(size); clearOrder() }
  const shift = (delta) => {
    clearOrder()
    const d = new Date(`${start}T12:00:00`)
    d.setDate(d.getDate() + delta * weeks * 7)
    setStart(isoDay(d))
  }

  const toggleExpanded = (id) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleHiddenMember = (id) => setHiddenMembers(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const save = async (project, row, monday, raw) => {
    const current = project.by_week[monday] || 0
    const hours = Math.max(0, Number(raw) || 0)
    if (Math.abs(hours - current) < 0.005) return      // nothing actually changed
    const key = `${project.project_id}:${row.team_member_id}:${monday}`
    setSaving(key)
    try {
      await setStaffingCell({
        projectId: project.project_id, teamMemberId: row.team_member_id,
        weekStart: monday, hours,
        scenarioId: canScenarios ? scenarioId : '',
      })
      load()          // totals, the person's week and the red all move together
    } catch (err) {
      setError(err.response?.data?.detail || k('saveFailed'))
    } finally {
      setSaving(null)
    }
  }

  if (error && !data) {
    return <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>
  }
  if (!data) return <p className="text-sm text-gray-400 px-1">{t('capacity.loading')}</p>

  const mondays = data.week_list.map(w => w.monday)
  const allRows = byExplicitOrder(data.rows, memberOrder, r => r.team_member_id)
  const visibleRows = allRows.filter(r => !hiddenMembers.has(r.team_member_id))

  const weekPlanned = (monday) => visibleRows.reduce(
    (s, r) => s + (r.by_week[monday] || 0), 0)
  const weekAvailable = (monday) => visibleRows.reduce(
    (s, r) => s + (r.week_available[monday] || 0), 0)

  const dragProps = (id) => ({
    draggable: true,
    onDragStart: () => setDragging(id),
    onDragEnd: () => setDragging(null),
    onDragOver: (e) => { if (dragging != null) e.preventDefault() },
    onDrop: () => {
      if (dragging == null || dragging === id) return
      setMemberOrder(o => reordered(
        o.length ? o : allRows.map(r => r.team_member_id), dragging, id))
      setDragging(null)
    },
  })

  const headCell = 'sticky top-0 z-20 bg-white border-b border-gray-200 px-2 py-2 text-center whitespace-nowrap font-medium text-gray-500'
  const nameCell = 'sticky left-0 z-10 px-3 py-1.5 whitespace-nowrap'

  return (
    <div className={fullscreen
      ? 'fixed inset-0 z-40 bg-gray-50 overflow-auto p-4 space-y-3'
      : 'space-y-4'}>
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          {WINDOWS.map(size => (
            <button key={size} onClick={() => goWindow(size)}
              title={k(`window_${size}_hint`)}
              className={`px-3 py-1.5 ${weeks === size ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k(`window_${size}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">‹</button>
          <span className="text-sm font-semibold text-gray-900 px-2 min-w-40 text-center">
            {weekLabel(data.start)} – {weekLabel(data.end)}
          </span>
          <button onClick={() => shift(1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">›</button>
        </div>

        {canScenarios && (
          <select
            value={scenarioId}
            onChange={e => setScenarioId(e.target.value)}
            className={`text-xs border rounded-lg px-2 py-1.5 bg-white ${
              scenarioId ? 'border-amber-300 text-amber-800' : 'border-gray-200 text-gray-700'}`}
          >
            <option value="">{k('realPlan')}</option>
            {scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}

        <input type="month" value={start.slice(0, 7)}
               title={k('monthPickHint')}
               onChange={e => {
                 if (!e.target.value) return
                 const [y, m] = e.target.value.split('-').map(Number)
                 const picked = monthWindow(y, m)
                 clearOrder()
                 setStart(picked.start)
                 setWeeks(picked.weeks)
               }}
               className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />

        <VisibilityMenu
          label={k('showPeople')}
          items={allRows}
          hidden={hiddenMembers}
          getKey={r => r.team_member_id}
          getLabel={r => r.name}
          getHint={r => num(r.total)}
          onToggle={toggleHiddenMember}
          onShowAll={() => setHiddenMembers(new Set())}
          onHideAll={() => setHiddenMembers(new Set(allRows.map(r => r.team_member_id)))}
          allLabel={k('showAll')}
          noneLabel={k('showNone')}
          emptyLabel={k('noStaffing')}
        />

        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setFullscreen(v => !v)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
            {fullscreen ? `⤡ ${k('fullscreenExit')}` : `⤢ ${k('fullscreen')}`}
          </button>
          <button
            onClick={() => exportStaffingPlan({ weeks, start, costBasis: 'company' }, password)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
            {k('export')}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
      {scenarioId ? (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {canEdit ? k('scenarioEditing') : k('scenarioReadOnly')}
        </p>
      ) : !canEdit && (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          {k('readOnly')}
        </p>
      )}

      <div className={`bg-white rounded-xl border border-gray-200 overflow-auto ${
        fullscreen ? 'max-h-[calc(100vh-13rem)]' : 'max-h-[65vh]'}`}>
        <table className="w-full text-xs border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={`${headCell} sticky left-0 z-30 text-left font-semibold text-gray-600`}
                  style={{ minWidth: 210 }}>
                {k('person')}
              </th>
              {data.week_list.map(w => (
                <th key={w.monday} className={headCell}
                    title={k('weekHint', { days: w.business_days })}>
                  <div className="leading-tight">S{w.week_number}</div>
                  <div className="text-[10px] font-normal text-gray-400 leading-tight">
                    {weekLabel(w.monday)}
                    {w.business_days < 5 && <span className="text-amber-600"> ·{w.business_days}</span>}
                  </div>
                </th>
              ))}
              <th className={`${headCell} font-semibold text-gray-600`}>{k('total')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => {
              const open = expanded.has(row.team_member_id)
              const stripe = i % 2 ? 'bg-gray-50/60' : 'bg-white'
              return [
                <tr key={row.team_member_id} className={stripe}>
                  <td {...dragProps(row.team_member_id)}
                      className={`${nameCell} bg-inherit text-gray-800 ${
                        dragging === row.team_member_id ? 'opacity-40' : ''}`}>
                    <button onClick={() => toggleExpanded(row.team_member_id)}
                            className="text-gray-400 hover:text-gray-900 mr-1.5"
                            title={k('expandHint')}>
                      {open ? '▾' : '▸'}
                    </button>
                    {row.name}
                  </td>
                  {mondays.map(monday => {
                    const planned = row.by_week[monday] || 0
                    const available = row.week_available[monday] || 0
                    // Over the week they actually have -- the finding this
                    // whole grid exists to surface.
                    const over = planned > available + 0.005
                    const fired = isFired(row.team_member_id, monday)
                    return (
                      <td key={monday}
                          className={`px-2 py-1.5 text-center tabular-nums ${
                            fired || over ? 'bg-red-50 text-red-700 font-semibold'
                              : planned ? 'text-gray-800' : 'text-gray-300'}`}
                          title={fired ? k('firedCellHint') : over
                            ? k('overHint', { planned: num(planned), available: num(available) })
                            : k('cellHint', { planned: num(planned), available: num(available) })}>
                        {planned ? num(planned) : '·'}
                      </td>
                    )
                  })}
                  <td className="px-2 py-1.5 text-center tabular-nums font-medium text-gray-900">
                    {num(row.total)}
                  </td>
                </tr>,
                ...(open ? row.projects.map(project => (
                  <tr key={`${row.team_member_id}-${project.project_id}`}
                      className="bg-blue-50/30">
                    <td className={`${nameCell} bg-blue-50/30 text-gray-500 pl-9`}
                        title={projectTitle(project)}>
                      {projectShortName(project)}
                    </td>
                    {mondays.map(monday => {
                      const value = project.by_week[monday] || 0
                      const key = `${project.project_id}:${row.team_member_id}:${monday}`
                      const fired = isFired(row.team_member_id, monday)
                      const locked = !canEdit || fired || saving === key
                      return (
                        <td key={monday} className="px-1 py-1 text-center">
                          <input
                            type="number" min="0" step="1"
                            defaultValue={value || ''}
                            disabled={locked}
                            title={fired ? k('firedCellHint') : undefined}
                            onBlur={e => save(project, row, monday, e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
                            className={`w-14 text-center tabular-nums rounded px-1 py-0.5 border focus:outline-none ${
                              fired ? 'border-transparent bg-red-50 text-red-700 font-semibold'
                                : canEdit ? 'border-transparent bg-transparent hover:border-gray-300 focus:border-gray-400 focus:bg-white disabled:text-gray-400'
                                : 'border-transparent bg-transparent disabled:text-gray-400'}`}
                          />
                        </td>
                      )
                    })}
                    <td className="px-2 py-1 text-center tabular-nums text-gray-500">
                      {num(project.total)}
                    </td>
                  </tr>
                )) : []),
              ]
            })}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={mondays.length + 2} className="px-4 py-8 text-center text-gray-400">
                  {k('noStaffing')}
                </td>
              </tr>
            )}
          </tbody>
          {visibleRows.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-gray-900 border-t border-gray-300">
                  {k('totalRow')}
                </td>
                {mondays.map(monday => {
                  const planned = weekPlanned(monday)
                  const available = weekAvailable(monday)
                  return (
                    <td key={monday}
                        className={`px-2 py-2 text-center tabular-nums border-t border-gray-300 ${
                          planned > available + 0.005 ? 'text-red-700' : 'text-gray-800'}`}>
                      {num(planned)}
                    </td>
                  )
                })}
                <td className="px-2 py-2 text-center tabular-nums text-gray-900 border-t border-gray-300">
                  {num(visibleRows.reduce((s, r) => s + r.total, 0))}
                </td>
              </tr>
              <tr className="bg-gray-50 text-gray-500">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-1.5"
                    title={k('utilisationHint')}>
                  {k('utilisationRow')}
                </td>
                {mondays.map(monday => {
                  const available = weekAvailable(monday)
                  const pct = available ? (100 * weekPlanned(monday)) / available : null
                  return (
                    <td key={monday}
                        className={`px-2 py-1.5 text-center tabular-nums ${
                          pct > 100 ? 'text-red-700 font-semibold'
                            : pct >= 90 ? 'text-amber-600' : ''}`}>
                      {pct == null ? '—' : `${num(pct, 0)}%`}
                    </td>
                  )
                })}
                <td className="px-2 py-1.5" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="text-[11px] text-gray-400">{k('legend')}</p>
    </div>
  )
}
